import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Typography, Grid, Divider, CircularProgress, Button } from '@mui/material';
import { Person, Edit, SwapVert } from '@mui/icons-material';

import GoBack from 'js/containers/GoBack.js';
import { downloadImage } from 'js/library/services/StorageManager.js';
import placeholderItem from 'styles/assets/placeholder/placeholder.gif';
import {
  getLastUserInfo,
  getUrlVariables,
  trackEventMatomo,
  trackEventMatomoVisit,
} from 'js/library/utils/helpers';
import { getUserStatusOrgs } from 'js/library/utils/API/getUserStatusOrgs';
import { insertUserOrgs } from 'js/library/utils/API/insertUserOrgs';
import { removeUserOrgs } from 'js/library/utils/API/removeUserOrgs';
import { getOrgByID } from 'js/library/utils/API/getOrgByID';
import { getCardsByOrgsNode } from 'js/library/utils/API/getCardsByOrgsNode';

import EditCards from 'js/containers/Organizations/EditCards';
import PendingRequests from 'js/containers/Organizations/PendingRequests';
import EditOrgInfo from 'js/containers/Organizations/EditOrgInfo';

import Cards from 'js/components/Organizations/Cards';

export default function Organizations(props) {
  const getVars = getUrlVariables();
  const userInfo = getLastUserInfo();
  const uId = userInfo.uId;

  const [id, setId] = useState(getVars.id);
  const [orgInfo, setOrgInfo] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [downloadedCoverImage, setDownloadedCoverImage] = useState(placeholderItem);
  const [cards, setCards] = useState(null);

  const [edit, setEdit] = useState(false);
  const [editOption, setEditOption] = useState(null);

  const { t } = useTranslation(); //Usado para tradução
  const history = useNavigate();

  useEffect(() => {
    if (orgInfo === null) {
      //tracking
      document.title = 'Organizacao';
      trackEventMatomoVisit('Organizacao');

      //buscar info org
      getOrgByID(userInfo.uId, id).then((resultOrg) => {
        if (resultOrg.success) {
          setOrgInfo(resultOrg.orgInfo);
        }
      });

      getUserStatusOrgs(userInfo.uId, id).then((resultOrg) => {
        if (resultOrg.success) {
          setUserStatus(resultOrg.status);
        }
      });

      getCardsByOrgsNode(userInfo.uId, id).then((result) => {
        if (!!result) {
          setCards(result.cards);
        }
      });
    }
  }, [orgInfo, userInfo, id, getVars.id, history]);

  const downloadImageOnLoad = (data) => {
    if (data !== undefined && data !== null) {
      downloadImage(data.split('/')[0], data.split('/')[1])
        .then((downloaded) => {
          setDownloadedCoverImage(downloaded);
        })
        .catch((error) => {
          return error;
        });
    }
  };

  const handleRequest = async () => {
    if (userInfo.triiboId.indexOf('@anonymous-triibo.com.br') >= 0) {
      props.setOpenState(true);
    } else {
      setUserStatus(null);

      await insertUserOrgs(uId, orgInfo.orgID);
      props.reloadOrgs(true);

      getUserStatusOrgs(uId, orgInfo.orgID).then((resultOrg) => {
        if (resultOrg.success) {
          setUserStatus(resultOrg.status);
        }
      });
    }
  };

  const handleRemoveRequest = async () => {
    if (userInfo.triiboId.indexOf('@anonymous-triibo.com.br') >= 0) {
      props.setOpenState(true);
    } else {
      setUserStatus(null);

      await removeUserOrgs(uId, orgInfo.orgID);
      props.reloadOrgs(true);

      getUserStatusOrgs(uId, orgInfo.orgID).then((resultOrg) => {
        if (resultOrg.success) {
          setUserStatus(resultOrg.status);
        }
      });
    }
  };

  const handleEdit = (option) => {
    setEditOption(option);
  };

  const renderEditOption = () => {
    if (editOption === 1) {
      return <PendingRequests id={orgInfo.orgID} uId={uId} handleEdit={handleEdit} />;
    } else if (editOption === 2) {
      let info = { ...orgInfo };
      info.downloadedCoverImage = downloadedCoverImage;
      return <EditOrgInfo handleEdit={handleEdit} orgInfo={info} />;
    } else if (editOption === 3) {
      return <EditCards cards={cards} handleEdit={handleEdit} />;
    }
  };

  const handleNewInfo = () => {
    setEdit(!edit);

    if (edit) {
      refreshContent();
    }
  };

  const refreshContent = () => {
    setId(getUrlVariables().id);
    setOrgInfo(null);
    setUserStatus(null);
    setCards(null);
    setDownloadedCoverImage(placeholderItem);

    //buscar info org
    getOrgByID(uId, getUrlVariables().id).then((resultOrg) => {
      if (resultOrg.success) setOrgInfo(resultOrg.orgInfo);
    });

    getUserStatusOrgs(uId, getUrlVariables().id).then((resultOrg) => {
      if (resultOrg.success) setUserStatus(resultOrg.status);
    });

    getCardsByOrgsNode(uId, getUrlVariables().id).then((result) => {
      try {
        if (!!result) {
          setCards(result.cards);
        }
      } catch (e) {
        setCards([]);
      }
    });
  };

  if (id !== getUrlVariables().id) refreshContent();

  return (
    <div>
      {orgInfo === null ? (
        <div style={{ textAlign: 'center' }} className="loading-content">
          <CircularProgress />
        </div>
      ) : !editOption ? (
        <div>
          <GoBack location="Organizacao" />

          <img
            src={downloadedCoverImage}
            onLoad={() => downloadImageOnLoad(orgInfo.coverImage)}
            alt="coverImage"
            width="100%"
          />

          <Typography style={{ fontWeight: 'bolder', color: 'black' }} variant="h6" gutterBottom>
            {orgInfo.name}
          </Typography>

          <Typography style={{ whiteSpace: 'pre-line' }} color="secondary" variant="subtitle1">
            {orgInfo.description}
          </Typography>

          <Divider style={{ margin: '20px 0px' }} />

          {userStatus === null ? (
            <div style={{ textAlign: 'center' }}>
              <CircularProgress />
            </div>
          ) : (
            <div>
              <Grid container spacing={3} style={{ width: '100%', marginBottom: '20px' }}>
                <Grid size={{ md: 4, xs: 12 }}></Grid>

                <Grid size={{ md: 4, xs: 12 }}>
                  {userStatus === 'non-participant' ||
                  userInfo.triiboId.indexOf('@anonymous-triibo.com.br') >= 0 ? (
                    <Button
                      fullWidth
                      onClick={() => {
                        handleRequest();
                        trackEventMatomo('Organizacao', 'tocar', 'botao', 'Juntar-se a triibo');
                      }}
                      variant="contained"
                      color="primary"
                      style={{ textTransform: 'none' }}
                    >
                      {t('organizations.join_triibo')}
                    </Button>
                  ) : userStatus === 'pending' && orgInfo.owner !== uId ? (
                    <Button
                      fullWidth
                      disabled
                      variant="contained"
                      color="primary"
                      style={{ textTransform: 'none' }}
                    >
                      {t('organizations.pending_invite')}
                    </Button>
                  ) : orgInfo.lockdown ? null : (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => {
                        handleRemoveRequest();
                        trackEventMatomo('Organizacao', 'tocar', 'botao', 'Sair da triibo');
                      }}
                      color="primary"
                      style={{ textTransform: 'none' }}
                    >
                      {t('organizations.leave_triibo')}
                    </Button>
                  )}
                </Grid>

                <Grid size={{ md: 4, xs: 12 }}>
                  {(orgInfo.owner === uId ||
                    (orgInfo.administratorsList !== undefined &&
                      orgInfo.administratorsList.indexOf(uId) >= 0)) &&
                  userStatus === 'participant' &&
                  editOption === null ? (
                    <Button
                      onClick={() => {
                        handleNewInfo();
                        trackEventMatomo(
                          'Organizacao',
                          'tocar',
                          'botao',
                          `${edit ? 'Cancelar' : 'Editar Triibo'}`
                        );
                      }}
                      fullWidth
                      variant="contained"
                      style={{
                        textTransform: 'none',
                        color: '#6D3195',
                        backgroundColor: '#F2F2F2',
                      }}
                    >
                      {edit ? t('organizations.cancel') : t('organizations.edit_triibo')}
                    </Button>
                  ) : null}
                </Grid>
              </Grid>

              <div>
                {cards === null ? (
                  <div style={{ textAlign: 'center' }} className="loading-content">
                    <CircularProgress />
                  </div>
                ) : edit ? (
                  <Grid container spacing={3} style={{ width: '100%' }}>
                    <Grid
                      style={orgInfo.accessMethod === 'public' ? { display: 'none' } : null}
                      size={{ md: 3, xs: 12 }}
                    >
                      <Button
                        fullWidth
                        onClick={() => {
                          handleEdit(1);
                          trackEventMatomo('Organizacao', 'tocar', 'botao', 'Aceitar Membros');
                        }}
                        variant="contained"
                        style={{
                          textTransform: 'none',
                          color: '#6D3195',
                          backgroundColor: '#F2F2F2',
                        }}
                      >
                        <Person size="2rem" />
                        <span style={{ fontSize: '10px' }}>
                          {t('organizations.accept_members')}
                        </span>
                      </Button>
                    </Grid>

                    <Grid size={{ md: 3, xs: 12 }}>
                      <Button
                        fullWidth
                        onClick={() => {
                          handleEdit(2);
                          trackEventMatomo('Organizacao', 'tocar', 'botao', 'Editar Informacoes');
                        }}
                        variant="contained"
                        style={{
                          textTransform: 'none',
                          color: '#6D3195',
                          backgroundColor: '#F2F2F2',
                        }}
                      >
                        <Edit size="2rem" />
                        <span style={{ fontSize: '10px' }}>
                          {t('organizations.edit_information')}
                        </span>
                      </Button>
                    </Grid>

                    <Grid size={{ md: 3, xs: 12 }}>
                      <Button
                        fullWidth
                        onClick={() => {
                          handleEdit(3);
                          trackEventMatomo('Organizacao', 'tocar', 'botao', 'Organizar Cards');
                        }}
                        variant="contained"
                        style={{
                          textTransform: 'none',
                          color: '#6D3195',
                          backgroundColor: '#F2F2F2',
                        }}
                      >
                        <SwapVert size="2rem" />
                        <span style={{ fontSize: '10px' }}>
                          {t('organizations.organize_cards')}
                        </span>
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  cards.map((card) => {
                    return (
                      <Cards cardInfo={card} key={card.cardId} origin="Organizacao" uId={uId} />
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        renderEditOption()
      )}
    </div>
  );
}
